import React, { useState, useEffect } from 'react';
import avulsaService from '../services/avulsaService';
import AvulsaFillForm from './AvulsaFillForm';
import AvulsaCheckout from './AvulsaCheckout';
import AvulsaResult from './AvulsaResult';
import Button from './ui/Button';
import './Login.css';

/**
 * Análise Avulsa V1 — rota pública /avulsa (pay-per-use, sem assinatura).
 *
 * Orquestrador da jornada, dirigido pelo workspace-status: landing+signup →
 * fill → checkout (Pix) → resultado. Rota TERMINAL (não entra no app logado);
 * usa token próprio (avulsa_token) via avulsaService.
 *
 * PR4a: landing + signup. PR4b: fill (AvulsaFillForm) + retomada via
 * workspace-status. PR4c (a fazer): checkout Pix + polling. PR4d: resultado.
 */
function AvulsaPage() {
  // phase: 'signup' | 'loading' | 'fill' | 'checkout' | 'result' (placeholder até PR4d)
  const [phase, setPhase] = useState(avulsaService.hasSession() ? 'loading' : 'signup');
  // numero_nc cacheado do fill (o workspace-status só devolve nc_id; o AvulsaCheckout
  // busca o próprio workspace-status, então o AvulsaPage não precisa guardá-lo).
  const [numeroNc, setNumeroNc] = useState(avulsaService.getNumeroNc());

  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [aceitoTermos, setAceitoTermos] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const senhaCurta = senha.length > 0 && senha.length < 8;
  // CNPJ saiu do signup (#287): é coletado no checkout (AvulsaCheckout), quando o
  // comprador vai pagar — menos fricção pro tráfego frio pago. Aqui é só
  // empresa + nome + e-mail + senha.

  useEffect(() => {
    document.title = 'Análise Avulsa · Qaa.sa';
    // Atribuição de anúncio: captura gclid/utm_* da URL no load (a LP de tráfego
    // pago anexa esses params ao link do app) → persiste → vai no signup.
    avulsaService.captureAttribution();
    if (avulsaService.hasSession()) refreshStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Eventos de funil do frontend (best-effort) — um por entrada de fase.
  useEffect(() => {
    if (phase === 'fill') avulsaService.event('fill_started');
    else if (phase === 'checkout') avulsaService.event('checkout_viewed');
    else if (phase === 'result') avulsaService.event('result_viewed');
  }, [phase]);

  // Decide a fase a partir do estado durável da compra (retomada pós-reload).
  async function refreshStatus() {
    try {
      const s = await avulsaService.workspaceStatus();
      if (!s.nc_id) setPhase('fill');          // ainda não preencheu a NC
      else if (s.status === 'completed') setPhase('result'); // análise já pronta
      else setPhase('checkout');               // preencheu → checkout (charge/polling/falha)
    } catch (err) {
      if (err.status === 401) {
        avulsaService.clearSession();
        setPhase('signup');
      } else {
        setPhase('fill'); // degrada pro fill — não trava o funil por falha transitória
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!aceitoTermos) {
      setError('É necessário aceitar os termos de uso para continuar.');
      return;
    }
    if (senhaCurta) {
      setError('A senha precisa ter ao menos 8 caracteres.');
      return;
    }
    setLoading(true);
    try {
      await avulsaService.signup({ nomeEmpresa, nome, email, senha, aceitoTermos });
      setPhase('fill');
    } catch (err) {
      setError(
        err.status === 409
          ? 'Este e-mail já tem uma conta. Use outro e-mail ou entre pela tela de login.'
          : (err.message || 'Não foi possível criar a conta. Tente novamente.')
      );
    } finally {
      setLoading(false);
    }
  };

  // ── carregando estado durável (retomada) ────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div className="login-page-wrapper">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 12 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 40, color: '#10b981', animation: 'spin 1s linear infinite' }}>progress_activity</span>
          <p className="form-subtitle">Carregando sua análise…</p>
        </div>
      </div>
    );
  }

  // ── preenchimento (PR4b) ────────────────────────────────────────────────────
  if (phase === 'fill') {
    return (
      <div className="login-page-wrapper" style={{ alignItems: 'flex-start', padding: '32px 16px', overflowY: 'auto' }}>
        <div style={{ width: '100%', maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <img src="/qaasa-texto-dark.png" alt="Qaa.sa" style={{ height: 28, marginBottom: 12 }} />
            <h2 className="form-title">Monte sua análise</h2>
            <p className="form-subtitle" style={{ maxWidth: 520, margin: '6px auto 0' }}>
              Preencha os dados da não-conformidade. É de graça — você só paga na próxima etapa
              para a IA gerar a investigação completa. O resultado é um ponto de partida que você valida.
            </p>
          </div>
          <AvulsaFillForm
            onFilled={(res) => {
              if (res.numero_nc) {
                avulsaService.rememberNumeroNc(res.numero_nc);
                setNumeroNc(res.numero_nc);
              }
              setPhase('checkout');
            }}
          />
        </div>
      </div>
    );
  }

  // ── checkout (PR4c): charge Pix + polling do workspace-status ───────────────
  if (phase === 'checkout') {
    return (
      <AvulsaCheckout
        numeroNc={numeroNc}
        onCompleted={() => setPhase('result')}
        onReview={() => setPhase('fill')}
      />
    );
  }

  // ── resultado (PR4d): relatório completo + baixar PDF + upsell ──────────────
  if (phase === 'result') {
    return (
      <AvulsaResult
        numeroNc={numeroNc}
        // #297 — recompra: o service já criou a nova compra (pending) e limpou os
        // caches por-compra; aqui só limpamos o número antigo e re-derivamos a
        // fase pelo workspace-status (nc_id=null → fill).
        onRepurchase={() => { setNumeroNc(null); setPhase('loading'); refreshStatus(); }}
      />
    );
  }

  // ── landing + signup (PR4a) ─────────────────────────────────────────────────
  return (
    <div className="login-page-wrapper">
      <div className="login-grid-container">
        {/* Esquerda — proposta + transparência pré-compra */}
        <div className="login-hero-column">
          <div className="hero-card-main">
            <div className="hero-content">
              <img src="/qaasa-texto-dark.png" alt="Qaa.sa" className="hero-logo" />
              <h1 className="hero-title">
                Uma análise completa de NC,<br />
                <span className="hero-title-accent">sem assinatura</span>
              </h1>
              <p className="hero-description">
                Descreva a não-conformidade, pague R$75 e a IA entrega a investigação:
                causa raiz (Ishikawa 6M + 5 porquês), plano de ação e um PDF pronto pra
                levar ao seu cliente ou auditoria.
              </p>
            </div>
            <div className="hero-decoration"></div>
          </div>

          <div className="feature-card">
            <span className="material-symbols-outlined feature-icon">description</span>
            <div>
              <h3 className="feature-title">O PDF é seu, pra sempre</h3>
              <p className="feature-description">
                Você baixa a análise completa e leva pra onde precisar. Pagamento único de R$75 — sem cartão recorrente.
              </p>
            </div>
          </div>

          <div className="feature-card-primary">
            <div className="feature-primary-content">
              <h3 className="feature-primary-title">Preencher é de graça.</h3>
              <p className="feature-primary-description">
                Monte a NC com calma e só pague pra gerar a análise. Análise pontual resolve uma RNC;
                quando quiser gerir a qualidade inteira (prazos, eficácia, histórico), é só assinar.{' '}
                <a href="https://qaasa.com.br/planos" target="_blank" rel="noopener noreferrer"
                   className="forgot-link" style={{ fontWeight: 600 }}>Conheça os planos →</a>
              </p>
            </div>
            <div className="feature-primary-icon">
              <span className="material-symbols-outlined">bolt</span>
            </div>
          </div>
        </div>

        {/* Direita — signup avulsa */}
        <div className="login-form-column">
          <div className="login-form-header">
            <h2 className="form-title">Comece sua análise</h2>
            <p className="form-subtitle">
              Crie uma conta rápida pra montar e acompanhar sua análise. Sem cartão agora.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="login-form-main">
            <div className="form-group">
              <label className="form-label">Nome da empresa</label>
              <div className="input-wrapper">
                <span className="material-symbols-outlined input-icon">apartment</span>
                <input
                  type="text" className="form-input" placeholder="Sua empresa LTDA"
                  value={nomeEmpresa} onChange={(e) => setNomeEmpresa(e.target.value)}
                  required disabled={loading}
                />
              </div>
              <p style={{ fontSize: 12, color: '#94a3b8', margin: '6px 2px 0' }}>
                Aparece como a empresa no cabeçalho do relatório.
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Seu nome</label>
              <div className="input-wrapper">
                <span className="material-symbols-outlined input-icon">person</span>
                <input
                  type="text" className="form-input" placeholder="Nome e sobrenome"
                  value={nome} onChange={(e) => setNome(e.target.value)}
                  required disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">E-mail</label>
              <div className="input-wrapper">
                <span className="material-symbols-outlined input-icon">mail</span>
                <input
                  type="email" className="form-input" placeholder="nome@empresa.com.br"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  required disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Senha</label>
              <div className="input-wrapper">
                <span className="material-symbols-outlined input-icon">lock</span>
                <input
                  type={showPassword ? 'text' : 'password'} className="form-input"
                  placeholder="Mínimo 8 caracteres"
                  value={senha} onChange={(e) => setSenha(e.target.value)}
                  required minLength={8} disabled={loading}
                />
                <button type="button" className="visibility-btn" onClick={() => setShowPassword(!showPassword)}>
                  <span className="material-symbols-outlined">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
              {senhaCurta && (
                <p style={{ fontSize: 12, color: '#dc2626', margin: '6px 2px 0' }}>
                  A senha precisa ter ao menos 8 caracteres.
                </p>
              )}
            </div>

            <div className="remember-wrapper">
              <input
                type="checkbox" id="avulsa-termos" className="remember-checkbox"
                checked={aceitoTermos} onChange={(e) => setAceitoTermos(e.target.checked)}
              />
              <label htmlFor="avulsa-termos" className="remember-label">
                Li e aceito os{' '}
                <a href="/termos" target="_blank" rel="noreferrer" className="forgot-link">Termos de Uso</a>{' '}
                e a{' '}
                <a href="/privacidade" target="_blank" rel="noreferrer" className="forgot-link">Política de Privacidade</a>.
              </label>
            </div>

            {error && <div className="error-message">{error}</div>}

            <Button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'CRIANDO...' : 'COMEÇAR ANÁLISE'}
              <span className="material-symbols-outlined" style={{ marginLeft: '0.5rem' }}>arrow_forward</span>
            </Button>
          </form>

          <div className="sso-section">
            <div className="sso-content">
              <p className="version-text">
                Já tem conta? <a href="/" className="forgot-link">Entrar</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AvulsaPage;
